import React, { useState } from 'react'

import {
  Body2,
  Box,
  Heading3,
  Heading5,
  Join,
  Space,
  Color,
  Icon,
  IconVariant,
  withCitrusTheme,
  JustifyContent,
  FlexDirection,
  Display,
  Body3,
  FontWeight,
  BorderStyle,
  AlignItems,
} from '@gousto-internal/citrus-react'
import moment from 'moment'

import { useWelcomeTracking } from '../hooks/useWelcomeTracking'
import { OrderDetails } from './components/OrderDetailsContainer'

type YourFirstBoxProps = {
  deliveryDate: string
  cutOffDate: string
  percentageOff: string
}

type YourFirstBoxHeaderProps = {
  deliveryDate: string
}

type YourFirstBoxSubHeaderProps = {
  cutOffDate: string
}

const formattedDeliveryDate = (deliveryDate: string) =>
  moment(deliveryDate, 'YYYY-MM-DD').format('dddd, MMM D')

const formattedCutOffDate = (cutOffDate: string) => moment(cutOffDate).format('dddd, Do of MMMM')

const CenteredAndClickableBox = withCitrusTheme(Box, () => ({
  margin: 'auto',
  cursor: 'pointer',
}))

const RoundedBox = withCitrusTheme(Box, () => ({
  borderRadius: 8,
}))

const LinkText = withCitrusTheme(Body3, (theme) => ({
  color: theme.tokens.colors.Secondary_400,
  fontWeight: FontWeight.Bold,
}))

const BoxWithMarginLeft = withCitrusTheme(Box, (theme) => ({
  [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: {
    marginLeft: 6,
  },
}))

const YourFirstBoxHeader = ({ deliveryDate }: YourFirstBoxHeaderProps) => (
  <Box
    display={Display.Flex}
    flexDirection={FlexDirection.Row}
    justifyContent={JustifyContent.SpaceBetween}
  >
    <Box display={Display.Flex} flexDirection={FlexDirection.Row}>
      <Box>
        <Heading3>Your first box</Heading3>
        <Space size={1} />
        <Body2>Arriving {formattedDeliveryDate(deliveryDate)}</Body2>
      </Box>
    </Box>
  </Box>
)

const YourFirstBoxSubHeader = ({ cutOffDate }: YourFirstBoxSubHeaderProps) => (
  <BoxWithMarginLeft>
    <Heading5>Order details</Heading5>
    <Space size={1} />
    <Body2>
      You can make changes to this order from your account until {formattedCutOffDate(cutOffDate)}.
    </Body2>
  </BoxWithMarginLeft>
)

const YourDiscountBox = ({ discount }: { discount: string }) => (
  <Box>
    <Box borderRadius={1.5} bg={Color.ColdGrey_800} paddingH={2} paddingV={0.5}>
      <Body3 fontWeight={FontWeight.Bold} color={Color.White}>
        {Math.trunc(Number(discount)) || ''}% off
      </Body3>
    </Box>
  </Box>
)

export const YourFirstBox = ({ deliveryDate, cutOffDate, percentageOff }: YourFirstBoxProps) => {
  const { trackUnfoldSummary } = useWelcomeTracking()
  const [isOrderSummaryExpanded, setOrderSummaryExpanded] = useState(false)

  const handleExpandClick = () => {
    setOrderSummaryExpanded(!isOrderSummaryExpanded)

    trackUnfoldSummary({ isOpen: !isOrderSummaryExpanded })
  }

  const renderTabletAndDesktopYourFirstBox = () => (
    <Box display={['none', 'none', 'block', 'block']}>
      <Join with={<Space size={6} />}>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          justifyContent={JustifyContent.SpaceBetween}
        >
          <Box display={Display.Flex} flexDirection={FlexDirection.Row}>
            <Box paddingRight={1}>
              <Icon name="success" size={10} />
            </Box>
            <YourFirstBoxHeader deliveryDate={deliveryDate} />
          </Box>
          <YourDiscountBox discount={percentageOff} />
        </Box>
        <YourFirstBoxSubHeader cutOffDate={cutOffDate} />
      </Join>
      <BoxWithMarginLeft>
        <OrderDetails />
      </BoxWithMarginLeft>
    </Box>
  )

  const renderMobileYourFirstBox = () => (
    <>
      <Box display={['flex', 'flex', 'none', 'none']} justifyContent={JustifyContent.SpaceBetween}>
        <Box display={Display.Flex} flexDirection={FlexDirection.Row}>
          <Box paddingRight={1}>
            <Icon name="success" size={8} />
          </Box>
          <Box>
            <YourFirstBoxHeader deliveryDate={deliveryDate} />
            <Space size={2} />
            <CenteredAndClickableBox
              role="button"
              aria-label="view order details"
              onClick={handleExpandClick}
              display={Display.Flex}
              flexDirection={FlexDirection.Row}
              alignItems={AlignItems.Center}
            >
              <LinkText size={2}>View order details</LinkText>
              <Space size={1} direction="horizontal" />
              <Icon
                name={!isOrderSummaryExpanded ? 'chevron_down' : 'chevron_up'}
                variant={IconVariant.Actionable}
                size={6}
              />
            </CenteredAndClickableBox>
          </Box>
        </Box>
        <YourDiscountBox discount={percentageOff} />
      </Box>
      {isOrderSummaryExpanded ? (
        <Box>
          <Space size={3} />
          <Box>
            <OrderDetails />
          </Box>
        </Box>
      ) : null}
    </>
  )

  return (
    <RoundedBox
      paddingH={[3, 5, 8, 8]}
      paddingV={[3, 5, 8, 8]}
      paddingRight={[3, 5, 8, 8]}
      bg={Color.White}
      borderWidth={0.5}
      borderStyle={BorderStyle.Solid}
      borderColor={Color.ColdGrey_100}
    >
      {renderTabletAndDesktopYourFirstBox()}
      {renderMobileYourFirstBox()}
    </RoundedBox>
  )
}
